import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { DollarSign, CreditCard, FileText, Trash2, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPayments = () => {
  const { token, user } = useAuth();

  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions' | 'invoices'

  // Transactions (payment_transactions)
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Invoices (invoices collection)
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Edit invoice UI state
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [editForm, setEditForm] = useState({
    amount: '',
    currency: 'gbp',
    status: 'pending',
    due_date: ''
  });
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);

  useEffect(() => {
    // load both so switching tabs is instant
    fetchPayments();
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCurrencySymbol = (currency) => {
    const c = (currency || 'gbp').toLowerCase();
    if (c === 'gbp') return '£';
    if (c === 'usd') return '$';
    if (c === 'eur') return '€';
    return '';
  };

  const formatMoney = (amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    const safe = Number(amount || 0);
    return `${symbol}${safe.toFixed(2)}`;
  };

  const parseDateSafe = (dateVal) => {
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? null : d;
  };

  // ---------- API calls ----------
  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      toast.error('Failed to fetch payment transactions');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      // If backend route not added yet, this makes it obvious:
      toast.error('Invoices endpoint not found. Add GET /api/admin/invoices in server.py');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const startEditInvoice = (invoice) => {
    setEditingInvoiceId(invoice.id);
    setEditForm({
      amount: String(invoice.amount ?? ''),
      currency: (invoice.currency || 'gbp').toLowerCase(),
      status: invoice.status || 'pending',
      // use YYYY-MM-DD for date input
      due_date: (() => {
        const d = parseDateSafe(invoice.due_date);
        if (!d) return '';
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      })()
    });
  };

  const cancelEdit = () => {
    setEditingInvoiceId(null);
    setEditForm({ amount: '', currency: 'gbp', status: 'pending', due_date: '' });
  };

  const saveInvoice = async () => {
    if (!editingInvoiceId) return;

    const amountNum = Number(editForm.amount);
    if (Number.isNaN(amountNum) || amountNum < 0) {
      toast.error('Amount must be a valid number');
      return;
    }
    if (!editForm.due_date) {
      toast.error('Due date is required');
      return;
    }

    setSavingInvoice(true);
    try {
      await axios.put(
        `${API}/admin/invoices/${editingInvoiceId}`,
        {
          amount: amountNum,
          currency: (editForm.currency || 'gbp').toLowerCase(),
          status: editForm.status,
          // server can store ISO string
          due_date: new Date(`${editForm.due_date}T00:00:00.000Z`).toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Invoice updated');
      await fetchInvoices();
      cancelEdit();
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    } finally {
      setSavingInvoice(false);
    }
  };

  const deleteInvoice = async (invoiceId) => {
    const ok = window.confirm('Delete this invoice? This will remove it from the client portal too.');
    if (!ok) return;

    setDeletingInvoiceId(invoiceId);
    try {
      await axios.delete(`${API}/admin/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice deleted');
      await fetchInvoices();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  // ---------- Stats ----------
  const totalRevenueGBP = useMemo(() => {
    // NOTE: your transactions store p.currency. If not, assume gbp.
    return payments
      .filter((p) => p.payment_status === 'paid')
      .filter((p) => (p.currency || 'gbp').toLowerCase() === 'gbp')
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  }, [payments]);

  const successfulCount = useMemo(
    () => payments.filter((p) => p.payment_status === 'paid').length,
    [payments]
  );

  const canManageInvoices = useMemo(() => {
    // Admin + Developer can edit/delete invoices. Outreach can view only.
    return user?.role === 'admin' || user?.role === 'developer';
  }, [user?.role]);

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="admin-payments-page">
      <ClientNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="payments-title">
            Payments
          </h1>
          <p className="text-slate-400">Track transactions and manage invoices</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8">
          <button
            onClick={() => setActiveTab('transactions')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'transactions'
                ? 'bg-indigo-500/20 text-indigo-300'
                : 'text-slate-300 hover:bg-slate-900'
            }`}
            data-testid="tab-transactions"
          >
            Transactions
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'invoices'
                ? 'bg-indigo-500/20 text-indigo-300'
                : 'text-slate-300 hover:bg-slate-900'
            }`}
            data-testid="tab-invoices"
          >
            Invoices
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="total-revenue">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <DollarSign className="text-green-400" size={20} />
              </div>
              <span className="text-slate-400">Total Revenue (GBP paid)</span>
            </div>
            <h3 className="text-3xl font-bold text-white">£{totalRevenueGBP.toFixed(2)}</h3>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="total-transactions">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <CreditCard className="text-indigo-400" size={20} />
              </div>
              <span className="text-slate-400">Transactions</span>
            </div>
            <h3 className="text-3xl font-bold text-white">{payments.length}</h3>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="successful-payments">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <CreditCard className="text-purple-400" size={20} />
              </div>
              <span className="text-slate-400">Successful</span>
            </div>
            <h3 className="text-3xl font-bold text-white">{successfulCount}</h3>
          </div>
        </div>

        {/* Transactions TAB */}
        {activeTab === 'transactions' && (
          <>
            {paymentsLoading ? (
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center text-slate-400">
                Loading transactions...
              </div>
            ) : payments.length === 0 ? (
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center" data-testid="no-payments">
                <CreditCard className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No payments yet</h3>
                <p className="text-slate-400">Payment transactions will appear here</p>
              </div>
            ) : (
              <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden" data-testid="payments-table">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Session ID</th>
                      <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Amount</th>
                      <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Status</th>
                      <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id} className="border-t border-slate-800" data-testid={`payment-${payment.id}`}>
                        <td className="py-4 px-6 text-slate-400 font-mono text-sm">
                          {(payment.session_id || '').substring(0, 20)}...
                        </td>
                        <td className="py-4 px-6 text-white font-semibold">
                          {formatMoney(payment.amount, payment.currency || 'gbp')}
                        </td>
                        <td className="py-4 px-6">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              payment.payment_status === 'paid'
                                ? 'bg-green-500/10 text-green-400'
                                : 'bg-yellow-500/10 text-yellow-400'
                            }`}
                          >
                            {payment.payment_status}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-slate-400 text-sm">
                          {payment.created_at ? format(new Date(payment.created_at), 'MMM dd, yyyy') : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Invoices TAB */}
        {activeTab === 'invoices' && (
          <>
            {invoicesLoading ? (
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center text-slate-400">
                Loading invoices...
              </div>
            ) : invoices.length === 0 ? (
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center" data-testid="no-invoices-admin">
                <FileText className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No invoices yet</h3>
                <p className="text-slate-400">Invoices will appear here</p>
              </div>
            ) : (
              <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden" data-testid="admin-invoices-table">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Client</th>
                      <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Amount</th>
                      <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Status</th>
                      <th className="text-left py-4 px-6 text-sm font-medium text-slate-300">Due</th>
                      <th className="text-right py-4 px-6 text-sm font-medium text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => {
                      const due = parseDateSafe(inv.due_date);
                      const isEditing = editingInvoiceId === inv.id;

                      return (
                        <React.Fragment key={inv.id}>
                          <tr className="border-t border-slate-800" data-testid={`invoice-admin-${inv.id}`}>
                            <td className="py-4 px-6 text-slate-300">
                              <div className="text-white font-medium">{inv.client_email || inv.client_id || 'Client'}</div>
                              <div className="text-slate-500 text-xs font-mono">{inv.id}</div>
                            </td>
                            <td className="py-4 px-6 text-white font-semibold">
                              {formatMoney(inv.amount, inv.currency)}
                            </td>
                            <td className="py-4 px-6">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  inv.status === 'paid'
                                    ? 'bg-green-500/10 text-green-400'
                                    : 'bg-yellow-500/10 text-yellow-400'
                                }`}
                              >
                                {inv.status}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-slate-400 text-sm">
                              {due ? format(due, 'MMM dd, yyyy') : 'N/A'}
                            </td>
                            <td className="py-4 px-6">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="secondary"
                                  className="bg-slate-900 text-white hover:bg-slate-800"
                                  onClick={() => startEditInvoice(inv)}
                                  disabled={!canManageInvoices}
                                  title={!canManageInvoices ? 'Only admin/developer can edit' : 'Edit invoice'}
                                  data-testid={`edit-invoice-${inv.id}`}
                                >
                                  <Pencil size={16} className="mr-2" />
                                  Edit
                                </Button>

                                <Button
                                  variant="destructive"
                                  className="bg-red-600/80 hover:bg-red-600 text-white"
                                  onClick={() => deleteInvoice(inv.id)}
                                  disabled={!canManageInvoices || deletingInvoiceId === inv.id}
                                  title={!canManageInvoices ? 'Only admin/developer can delete' : 'Delete invoice'}
                                  data-testid={`delete-invoice-${inv.id}`}
                                >
                                  <Trash2 size={16} className="mr-2" />
                                  {deletingInvoiceId === inv.id ? 'Deleting...' : 'Delete'}
                                </Button>
                              </div>
                            </td>
                          </tr>

                          {isEditing && (
                            <tr className="border-t border-slate-800 bg-slate-950/50">
                              <td colSpan={5} className="py-5 px-6">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Amount</label>
                                    <Input
                                      value={editForm.amount}
                                      onChange={(e) => setEditForm((p) => ({ ...p, amount: e.target.value }))}
                                      className="bg-slate-950 border-slate-800 text-white"
                                      placeholder="e.g. 49.00"
                                      inputMode="decimal"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Currency</label>
                                    <select
                                      value={editForm.currency}
                                      onChange={(e) => setEditForm((p) => ({ ...p, currency: e.target.value }))}
                                      className="w-full rounded-md bg-slate-950 border border-slate-800 text-white px-3 py-2"
                                    >
                                      <option value="gbp">GBP (£)</option>
                                      <option value="usd">USD ($)</option>
                                      <option value="eur">EUR (€)</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Status</label>
                                    <select
                                      value={editForm.status}
                                      onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                                      className="w-full rounded-md bg-slate-950 border border-slate-800 text-white px-3 py-2"
                                    >
                                      <option value="pending">pending</option>
                                      <option value="paid">paid</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Due date</label>
                                    <Input
                                      type="date"
                                      value={editForm.due_date}
                                      onChange={(e) => setEditForm((p) => ({ ...p, due_date: e.target.value }))}
                                      className="bg-slate-950 border-slate-800 text-white"
                                    />
                                  </div>
                                </div>

                                <div className="flex justify-end gap-2 mt-4">
                                  <Button
                                    onClick={cancelEdit}
                                    variant="secondary"
                                    className="bg-slate-900 text-white hover:bg-slate-800"
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={saveInvoice}
                                    disabled={savingInvoice}
                                    className="bg-white text-black hover:bg-slate-200"
                                    data-testid="save-invoice-btn"
                                  >
                                    {savingInvoice ? 'Saving...' : 'Save Changes'}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPayments;
